import React from 'react';
import AdminPageLoader from '../../../shared/ui/admin-loader/AdminPageLoader';
import ImageUpload from '../../../shared/ui/image-upload/ImageUpload';
import TranslateButton from '../../../shared/ui/translate-button/TranslateButton';
import { hint, input, label as labelStyle } from '../../../shared/ui/formStyles';
import { slugify } from '../../../shared/lib/slugify';
import { collectLocalizedPairs } from '../../../shared/lib/localizedPairs';
import { useAutoTranslate } from '../../../shared/lib/useAutoTranslate';
import toast from '../../../shared/lib/toast';
import { useProgramsContentForm } from '../lib/useProgramsContentForm';
import ItemsEditor from './ItemsEditor';
import LocalizedField from './LocalizedField';

const emptyText = () => ({ ua: '', en: '' });

const Section = ({ number, title, description, children, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', background: '#fff', marginBottom: '14px' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'baseline',
          gap: '10px',
          padding: '16px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: '12px', color: '#94A3B8' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
          {`${number}. ${title}`}
        </span>
        {description && (
          <span style={{ fontSize: '12px', color: '#94A3B8' }}>{description}</span>
        )}
      </button>
      {open && <div style={{ padding: '0 18px 20px' }}>{children}</div>}
    </div>
  );
};

// Порожній розділ на сайті замінюється вбудованим текстом, і це збиває:
// на сторінці текст видно, а в адмінці порожньо. Кажемо про це прямо.
const FallbackNotice = ({ isEmpty }) =>
  isEmpty ? (
    <div
      style={{
        padding: '10px 14px',
        marginBottom: '14px',
        background: '#FFF7ED',
        border: '1px solid #FED7AA',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#9A3412',
      }}
    >
      Розділ порожній — зараз на сторінці показується типовий текст, зашитий у сайт.
      Заповніть поля, щоб замінити його своїм.
    </div>
  ) : null;

const TextRow = ({ title, description, value, onChange, placeholder }) => (
  <div style={{ marginBottom: '16px' }}>
    <span style={labelStyle}>{title}</span>
    {description && <p style={{ ...hint, margin: '0 0 8px' }}>{description}</p>}
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={input}
    />
  </div>
);

const ProgramsContentForm = ({ initialData, mode = 'edit' }) => {
  const formik = useProgramsContentForm(initialData, { mode });
  const set = (path, value) => formik.setFieldValue(path, value);
  const values = formik.values;
  const isCreate = mode === 'create';
  // Поки програму створюють, адреса підставляється з назви автоматично;
  // щойно її поправили руками — більше не перезаписуємо.
  const slugTouchedRef = React.useRef(false);
  const { translateFields, isTranslating } = useAutoTranslate(formik);

  // Перекладаємо всю форму одразу: полів кілька десятків і вони лежать у
  // списках, тому шляхи збираємо обходом значень, а не перелічуємо руками.
  // Заповнену англійську не чіпаємо — у teens і adults вона написана вручну.
  const handleTranslate = async () => {
    const { pairs, filled } = collectLocalizedPairs(values, { onlyEmptyTarget: true });

    if (pairs.length === 0) {
      toast.success(
        filled > 0
          ? 'Усі англійські поля вже заповнені'
          : 'Немає що перекладати: спочатку заповніть українські поля'
      );
      return;
    }

    const { translated, failed } = await translateFields(pairs);

    if (failed > 0) {
      toast.error(`Перекладено ${translated}, не вдалося ${failed}. Спробуйте ще раз.`);
      return;
    }

    toast.success(`Перекладено полів: ${translated}. Перевірте й збережіть.`);
  };

  const isTextEmpty = (text) => !(text?.ua?.trim() || text?.en?.trim());
  const highlightIsEmpty =
    isTextEmpty(values.highlightSection.title) &&
    isTextEmpty(values.highlightSection.description) &&
    (values.highlightSection.audienceItems || []).length === 0 &&
    (values.highlightSection.reasonsItems || []).length === 0;
  const finalCtaIsEmpty =
    isTextEmpty(values.finalCta.title) &&
    isTextEmpty(values.finalCta.description) &&
    isTextEmpty(values.finalCta.primaryCta.label);
  const overviewFactsAreEmpty = (values.overview.facts || []).length === 0;

  const handleTitleChange = (title) => {
    set('title', title);

    if (isCreate && !slugTouchedRef.current) {
      set('slug', slugify(title.ua || title.en || ''));
    }
  };

  return (
    <form id="programs-content-form" onSubmit={formik.handleSubmit} className="programs-create-form">
      {formik.isSubmitting && <AdminPageLoader label="Збереження програми..." fullScreen />}

      <div
        style={{
          padding: '14px 16px',
          background: '#EEF4FF',
          border: '1px solid #C7D8FF',
          borderRadius: '10px',
          fontSize: '13px',
          color: '#1E3A8A',
          marginBottom: '16px',
        }}
      >
        {isCreate
          ? 'Достатньо заповнити перший розділ і зберегти — програма одразу зʼявиться на сайті. Решту розділів можна дозаповнити потім: порожні блоки сайт замінює типовим текстом.'
          : 'Тут зібрано все, що показує сторінка програми — зверху вниз, у тому ж порядку. Розділи можна згортати. Порожній розділ на сайт не потрапляє: замість нього показується типовий текст.'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <TranslateButton
          onClick={handleTranslate}
          isLoading={isTranslating}
          label="Перекласти порожні поля UA → EN"
        />
        <span style={{ ...hint, margin: 0 }}>
          Заповнить англійські поля в усіх розділах. Уже написану англійську не змінює.
        </span>
      </div>

      <Section
        number="1"
        title="Картка на головній"
        description="як програма виглядає у списку"
        defaultOpen
      >
        <LocalizedField
          title="Назва програми"
          value={values.title}
          onChange={handleTitleChange}
          placeholder="UBC Kids"
        />

        <div style={{ marginBottom: '16px' }}>
          <span style={labelStyle}>Адреса сторінки</span>
          <p style={{ ...hint, margin: '0 0 8px' }}>
            {isCreate
              ? 'Підставляється з назви автоматично. Латиниця, цифри й дефіс.'
              : 'Змінювати не варто: за цією адресою на сторінку вже можуть вести посилання.'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#94A3B8', whiteSpace: 'nowrap' }}>
              ubcollege.com.ua/ua/course/
            </span>
            <input
              type="text"
              value={values.slug || ''}
              placeholder="ubc-kids"
              onChange={(e) => {
                slugTouchedRef.current = true;
                set('slug', slugify(e.target.value));
              }}
              style={input}
            />
          </div>
        </div>
        <LocalizedField
          title="Вікова категорія"
          description="Підпис під назвою у списку програм."
          value={values.ageLabel}
          onChange={(v) => set('ageLabel', v)}
          placeholder="6–12 років"
        />
        <LocalizedField
          title="Короткий опис"
          rows={3}
          value={values.summary}
          onChange={(v) => set('summary', v)}
        />
        <LocalizedField
          title="Текст кнопки на картці"
          value={values.ctaLabel}
          onChange={(v) => set('ctaLabel', v)}
          placeholder="Дізнатись більше"
        />

        <ImageUpload
          value={values.coverImageUrl}
          onChange={(url) => set('coverImageUrl', url)}
          label="Обкладинка програми"
          uploadPath="programs/covers"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '18px', alignItems: 'start', marginTop: '10px' }}>
          <div>
            <span style={labelStyle}>Порядок у списку</span>
            <input
              type="number"
              value={values.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              style={input}
            />
            <p style={hint}>Менше число — вище у списку.</p>
          </div>
          <div style={{ display: 'flex', gap: '20px', paddingTop: '22px' }}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={values.isFeatured}
                onChange={(e) => set('isFeatured', e.target.checked)}
              />
              Виділена програма
            </label>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={values.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              Показувати на сайті
            </label>
          </div>
        </div>
      </Section>

      <Section number="2" title="Верхній блок сторінки" description="перший екран курсу">
        <LocalizedField
          title="Малий текст над заголовком"
          value={values.hero.eyebrow}
          onChange={(v) => set('hero.eyebrow', v)}
          placeholder="КУРС ДЛЯ ДІТЕЙ 6–12 РОКІВ"
        />
        <LocalizedField
          title="Великий заголовок"
          value={values.hero.title}
          onChange={(v) => set('hero.title', v)}
        />
        <LocalizedField
          title="Виділене слово в заголовку"
          description="Це слово має бути всередині заголовка — воно підсвітиться кольором."
          value={values.hero.highlightedWord}
          onChange={(v) => set('hero.highlightedWord', v)}
        />
        <LocalizedField
          title="Опис під заголовком"
          rows={4}
          value={values.hero.description}
          onChange={(v) => set('hero.description', v)}
        />
        <LocalizedField
          title="Текст кнопки"
          value={values.hero.primaryCta.label}
          onChange={(v) => set('hero.primaryCta.label', v)}
        />
        <TextRow
          title="Куди веде кнопка"
          description="Шлях усередині сайту, наприклад /placement-test."
          value={values.hero.primaryCta.url}
          onChange={(v) => set('hero.primaryCta.url', v)}
          placeholder="/placement-test"
        />
      </Section>

      <Section number="3" title="Про навчання" description="текст зліва і табличка справа">
        <LocalizedField
          title="Заголовок розділу"
          value={values.overview.title}
          onChange={(v) => set('overview.title', v)}
          placeholder="ПРО НАВЧАННЯ"
        />

        <span style={labelStyle}>Абзаци тексту</span>
        <ItemsEditor
          items={values.overview.paragraphs}
          onChange={(list) => set('overview.paragraphs', list)}
          itemName="абзац"
          addLabel="Додати абзац"
          emptyText="Поки жодного абзацу — натисніть «Додати абзац»."
          createItem={emptyText}
          renderItem={(item, onItemChange) => (
            <LocalizedField rows={4} value={item} onChange={onItemChange} />
          )}
        />

        <div style={{ marginTop: '22px' }}>
          <span style={labelStyle}>Табличка з характеристиками</span>
          <FallbackNotice isEmpty={overviewFactsAreEmpty} />
          <ItemsEditor
            items={values.overview.facts}
            onChange={(list) => set('overview.facts', list)}
            itemName="рядок"
            addLabel="Додати рядок таблички"
            description="Праворуч від тексту: ліворуч назва, праворуч значення. Наприклад «ЗАНЯТТЯ → 2 РАЗИ НА ТИЖДЕНЬ», «ТРИВАЛІСТЬ → 90 ХВИЛИН»."
            emptyText="Табличка порожня — на сайті замість неї показуються типові значення."
            createItem={() => ({ label: emptyText(), value: emptyText() })}
            renderItem={(item, onItemChange) => (
              <>
                <LocalizedField
                  title="Назва (ліворуч)"
                  value={item.label}
                  onChange={(label) => onItemChange({ ...item, label })}
                  placeholder="ТРИВАЛІСТЬ"
                />
                <LocalizedField
                  title="Значення (праворуч)"
                  value={item.value}
                  onChange={(value) => onItemChange({ ...item, value })}
                  placeholder="90 ХВИЛИН"
                />
              </>
            )}
          />
        </div>
      </Section>

      <Section number="4" title="Як ми вчимо" description="методика, картки з навичками">
        <LocalizedField
          title="Заголовок розділу"
          value={values.methodology.title}
          onChange={(v) => set('methodology.title', v)}
          placeholder="ЯК МИ ВЧИМО"
        />
        <LocalizedField
          title="Опис під заголовком"
          rows={3}
          value={values.methodology.description}
          onChange={(v) => set('methodology.description', v)}
        />

        <ItemsEditor
          items={values.methodology.items}
          onChange={(list) => set('methodology.items', list)}
          itemName="пункт"
          addLabel="Додати пункт методики"
          emptyText="Пунктів немає — на сайті буде типовий набір."
          createItem={(index) => ({
            step: { ua: String(index + 1).padStart(2, '0'), en: String(index + 1).padStart(2, '0') },
            title: emptyText(),
            text: emptyText(),
          })}
          renderItem={(item, onItemChange) => (
            <>
              <TextRow
                title="Номер"
                description="Показується великим цифрою на картці."
                value={item.step?.ua || ''}
                onChange={(step) => onItemChange({ ...item, step: { ua: step, en: step } })}
                placeholder="01"
              />
              <LocalizedField
                title="Назва навички"
                value={item.title}
                onChange={(title) => onItemChange({ ...item, title })}
                placeholder="SPEAKING"
              />
              <LocalizedField
                title="Опис"
                rows={3}
                value={item.text}
                onChange={(text) => onItemChange({ ...item, text })}
              />
            </>
          )}
        />
      </Section>

      <Section number="5" title="Після курсу" description="результати навчання">
        <LocalizedField
          title="Заголовок розділу"
          value={values.outcomes.title}
          onChange={(v) => set('outcomes.title', v)}
          placeholder="ПІСЛЯ КУРСУ ВИ"
        />
        <LocalizedField
          title="Опис під заголовком"
          description="Показується між заголовком розділу і картками результатів."
          rows={3}
          value={values.outcomes.description}
          onChange={(v) => set('outcomes.description', v)}
        />

        <ItemsEditor
          items={values.outcomes.items}
          onChange={(list) => set('outcomes.items', list)}
          itemName="результат"
          addLabel="Додати результат"
          emptyText="Результатів немає — на сайті буде типовий набір."
          createItem={(index) => ({
            step: { ua: String(index + 1).padStart(2, '0'), en: String(index + 1).padStart(2, '0') },
            label: emptyText(),
            title: emptyText(),
            subtitle: emptyText(),
            isHighlighted: false,
          })}
          renderItem={(item, onItemChange) => (
            <>
              <TextRow
                title="Номер"
                value={item.step?.ua || ''}
                onChange={(step) => onItemChange({ ...item, step: { ua: step, en: step } })}
                placeholder="01"
              />
              <LocalizedField
                title="Підпис зверху"
                value={item.label}
                onChange={(label) => onItemChange({ ...item, label })}
                placeholder="Speaking"
              />
              <LocalizedField
                title="Заголовок"
                value={item.title}
                onChange={(title) => onItemChange({ ...item, title })}
              />
              <LocalizedField
                title="Опис"
                rows={2}
                value={item.subtitle}
                onChange={(subtitle) => onItemChange({ ...item, subtitle })}
              />
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(item.isHighlighted)}
                  onChange={(e) => onItemChange({ ...item, isHighlighted: e.target.checked })}
                />
                Виділити цю картку кольором
              </label>
            </>
          )}
        />
      </Section>

      <Section number="6" title="Кому підходить і чому обирають" description="два списки">
        <FallbackNotice isEmpty={highlightIsEmpty} />
        <LocalizedField
          title="Заголовок розділу"
          value={values.highlightSection.title}
          onChange={(v) => set('highlightSection.title', v)}
        />
        <LocalizedField
          title="Опис під заголовком"
          rows={3}
          value={values.highlightSection.description}
          onChange={(v) => set('highlightSection.description', v)}
        />

        <div style={{ marginTop: '18px' }}>
          <LocalizedField
            title="Заголовок першого списку"
            value={values.highlightSection.audienceTitle}
            onChange={(v) => set('highlightSection.audienceTitle', v)}
            placeholder="Кому підходить курс"
          />
          <ItemsEditor
            items={values.highlightSection.audienceItems}
            onChange={(list) => set('highlightSection.audienceItems', list)}
            itemName="рядок"
            addLabel="Додати рядок до першого списку"
            emptyText="Список порожній."
            createItem={emptyText}
            renderItem={(item, onItemChange) => (
              <LocalizedField value={item} onChange={onItemChange} />
            )}
          />
        </div>

        <div style={{ marginTop: '22px' }}>
          <LocalizedField
            title="Заголовок другого списку"
            value={values.highlightSection.reasonsTitle}
            onChange={(v) => set('highlightSection.reasonsTitle', v)}
            placeholder="Чому обирають UBC"
          />
          <ItemsEditor
            items={values.highlightSection.reasonsItems}
            onChange={(list) => set('highlightSection.reasonsItems', list)}
            itemName="рядок"
            addLabel="Додати рядок до другого списку"
            emptyText="Список порожній."
            createItem={emptyText}
            renderItem={(item, onItemChange) => (
              <LocalizedField value={item} onChange={onItemChange} />
            )}
          />
        </div>
      </Section>

      <Section number="7" title="Заклик у кінці сторінки" description="останній блок з кнопками">
        <FallbackNotice isEmpty={finalCtaIsEmpty} />
        <LocalizedField
          title="Заголовок"
          value={values.finalCta.title}
          onChange={(v) => set('finalCta.title', v)}
          placeholder="Готові почати?"
        />
        <LocalizedField
          title="Опис"
          rows={3}
          value={values.finalCta.description}
          onChange={(v) => set('finalCta.description', v)}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
          <div>
            <LocalizedField
              title="Головна кнопка"
              value={values.finalCta.primaryCta.label}
              onChange={(v) => set('finalCta.primaryCta.label', v)}
            />
            <TextRow
              title="Куди веде"
              value={values.finalCta.primaryCta.url}
              onChange={(v) => set('finalCta.primaryCta.url', v)}
              placeholder="/placement-test"
            />
          </div>
          <div>
            <LocalizedField
              title="Друга кнопка (необовʼязково)"
              value={values.finalCta.secondaryCta.label}
              onChange={(v) => set('finalCta.secondaryCta.label', v)}
            />
            <TextRow
              title="Куди веде"
              value={values.finalCta.secondaryCta.url}
              onChange={(v) => set('finalCta.secondaryCta.url', v)}
              placeholder="/contact"
            />
          </div>
        </div>
      </Section>

      {Object.keys(formik.errors).length > 0 && (
        <div
          style={{
            padding: '14px 16px',
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#B91C1C',
          }}
        >
          <strong>Не заповнено обовʼязкове:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
            {Object.entries(formik.errors).map(([key, value]) => (
              <li key={key}>
                {key}: {typeof value === 'string' ? value : JSON.stringify(value)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};

export default ProgramsContentForm;
